import { Copyright, Globe, Mail, LifeBuoy, MessageCircle, Check, ArrowLeft, MousePointerClick, Instagram, Music2, Shield, BookOpen, Sparkles, Info, Compass, Database, FileText, Package, Scale, RefreshCcw } from "lucide-react";
import { Link as RouterLink } from "react-router-dom";
import { LogoMark } from "@/components/Logo";

const SITE_URL = "https://prolancer.sa";
const SITE_DISPLAY = "www.prolancer.sa";
const EMAIL_INFO = "info@pro-lancer.net";
const EMAIL_SUPPORT = "info@pro-lancer.net";
const WHATSAPP = "+966 53 255 6796";
const WHATSAPP_LINK = "https://wa.me/966532556796";
const INSTAGRAM_URL = "https://www.instagram.com/prolancerplatform";
const INSTAGRAM_HANDLE = "@prolancerplatform";
const TIKTOK_URL = "https://www.tiktok.com/@prolancer.platform";
const TIKTOK_HANDLE = "@prolancer.platform";
const OFFICIAL_HASHTAG = "#برولانسر_السعودية";

// QR image (static asset already generated for the brand card)
const QR_URL =
  "https://api.qrserver.com/v1/create-qr-code/?size=240x240&margin=0&color=0e0e12&bgcolor=18c98f&data=" +
  encodeURIComponent(SITE_URL);

function ContactRow({ Icon, label, value, href, dir = "ltr" }) {
  return (
    <a
      href={href}
      target={href.startsWith("http") ? "_blank" : undefined}
      rel="noopener noreferrer"
      className="flex items-center gap-3 group"
    >
      <span className="h-11 w-11 rounded-full border border-[#18c98f]/40 bg-[#18c98f]/10 flex items-center justify-center shrink-0 group-hover:bg-[#18c98f]/20 transition">
        <Icon className="h-5 w-5 text-[#18c98f]" />
      </span>
      <div className="min-w-0">
        <div className="text-xs text-white/60 font-arabic">{label}</div>
        <div className="text-sm font-semibold text-[#18c98f] font-mono truncate" dir={dir}>
          {value}
        </div>
      </div>
    </a>
  );
}

function Feature({ label }) {
  return (
    <div className="flex items-center gap-1.5 text-xs text-white/80 font-arabic">
      <span className="h-5 w-5 rounded-full border border-[#18c98f]/50 flex items-center justify-center">
        <Check className="h-3 w-3 text-[#18c98f]" />
      </span>
      {label}
    </div>
  );
}

export default function Footer({ lang = "ar", theme = "dark" }) {
  const dark = theme === "dark";
  const ar = lang === "ar";

  return (
    <footer
      data-testid="site-footer"
      className={`no-print border-t ${
        dark ? "bg-[#182138] border-[#2e3a5c] text-white" : "bg-white border-[#e5e5ea] text-[#2d2c35]"
      }`}
      dir={ar ? "rtl" : "ltr"}
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        {/* HTML brand card (replaces the old rasterised PNG so emails/text stay editable) */}
        <div
          data-testid="brand-card"
          className="mx-auto max-w-4xl rounded-3xl border border-[#2e3a5c] bg-[#1f2a47] shadow-[0_20px_60px_-20px_rgba(24,201,143,0.15)] p-6 sm:p-8 lg:p-10"
          dir="rtl"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-stretch">
            {/* LEFT COLUMN — copy + contact rows */}
            <div className="flex flex-col gap-6">
              <div className="font-arabic text-white leading-relaxed">
                <p className="text-lg font-bold">
                  منصة <span className="text-[#18c98f]">برولانسر</span> تجمع بين
                </p>
                <p className="text-lg font-bold">
                  أصحاب المشاريع وأفضل
                </p>
                <p className="text-lg font-bold">
                  المستقلين في <span className="text-[#18c98f]">المملكة العربية السعودية</span>.
                </p>
                <p className="mt-3 text-sm text-white/70">
                  نسهل عليك إيجاد الكفاءات المناسبة لمشروعك.
                </p>
              </div>

              {/* Duplicate monochrome icon rail removed — brand-colored socials
                  now live in the bottom row below the sitemap grid. */}

              {/* Official hashtag badge */}
              <a
                href="https://www.google.com/search?q=%23%D8%A8%D8%B1%D9%88%D9%84%D8%A7%D9%86%D8%B3%D8%B1_%D8%A7%D9%84%D8%B3%D8%B9%D9%88%D8%AF%D9%8A%D8%A9"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-hashtag"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#18c98f]/15 border border-[#18c98f]/40 text-[#18c98f] text-sm font-bold hover:bg-[#18c98f]/25 transition w-fit"
                aria-label={`الهاشتاق الرسمي ${OFFICIAL_HASHTAG}`}
              >
                <span className="font-arabic">{OFFICIAL_HASHTAG}</span>
              </a>
            </div>

            {/* RIGHT COLUMN — logo + CTA + QR */}
            <div className="flex flex-col items-center justify-between gap-6">
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <div className="text-4xl font-bold text-white font-arabic">برولانسر</div>
                  <div className="mt-1 flex items-center gap-2 justify-end">
                    <span className="inline-block h-[3px] w-10 rounded-full bg-[#18c98f]" />
                    <span className="text-[11px] font-semibold tracking-[0.35em] text-[#b4b4be]">
                      PROLANCER
                    </span>
                  </div>
                </div>
                <LogoMark size={64} />
              </div>

              <div className="rounded-2xl border border-[#2e3a5c] bg-[#182138] p-5 w-full text-center">
                <div className="text-xl font-bold text-white font-arabic">ابدأ فرصتك اليوم.</div>
                <div className="mt-1 text-xs text-white/70 font-arabic leading-relaxed">
                  سجل مجانًا في برولانسر
                  <br />
                  وابدأ رحلتك نحو النجاح.
                </div>

                {/* Clickable QR — behaves as a large CTA button */}
                <a
                  href={SITE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="افتح موقع برولانسر www.prolancer.sa"
                  data-testid="footer-qr-cta"
                  className="group mt-4 mx-auto block w-fit rounded-2xl bg-[#18c98f] p-2 shadow-[0_10px_30px_-10px_rgba(24,201,143,0.6)] transition-all duration-300 hover:scale-[1.04] hover:shadow-[0_18px_40px_-12px_rgba(24,201,143,0.8)] active:scale-[0.98] cursor-pointer relative"
                >
                  <img
                    src={QR_URL}
                    alt="امسح للانتقال إلى prolancer.sa"
                    className="block rounded-lg"
                    width="140"
                    height="140"
                    loading="lazy"
                  />
                  {/* Hover overlay — hints that the QR is clickable */}
                  <span className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-2xl bg-[#182138]/0 group-hover:bg-[#182138]/70 transition-colors duration-300">
                    <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center gap-1.5 rounded-full bg-[#18c98f] px-3 py-1.5 text-[11px] font-bold text-[#182138] font-arabic">
                      <MousePointerClick className="h-3.5 w-3.5" />
                      اضغط للدخول
                    </span>
                  </span>
                </a>

                {/* CTA line — clear call to start now */}
                <a
                  href={SITE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-start-cta"
                  className="mt-4 group inline-flex items-center gap-2 px-5 py-2.5 rounded-[14px] bg-[#18c98f] text-white text-sm font-extrabold font-arabic shadow-[0_10px_25px_-10px_rgba(24,201,143,0.7)] hover:bg-[#18c98f] hover:scale-[1.03] active:scale-[0.98] transition-all"
                >
                  <span>ابدأ الآن مع برولانسر</span>
                  <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-1" />
                </a>

                <div className="mt-3 flex w-fit items-center gap-2 text-[11px] font-mono text-white/60" dir="ltr">
                  <Globe className="h-3.5 w-3.5 text-[#18c98f]" />
                  www.prolancer.sa
                </div>
              </div>
            </div>
          </div>

          {/* Feature strip removed per user request. */}

          {/* Sitemap — the four full-page sections rendered as a compact 2×2
              (sm 1×4) grid, followed by a second long-rectangle grid listing
              the five official Saudi freelance resources with their full names. */}
          <div
            data-testid="footer-sitemap"
            className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-2"
            dir="rtl"
          >
            {[
              { to: "/market", Icon: Database, label: ar ? "مرصد السوق · بيانات حيّة" : "Market Observer · live data", test: "footer-link-market" },
              { to: "/guides", Icon: Compass, label: ar ? "أدلّة أسعار الخدمات" : "Pricing guides", test: "footer-link-guides" },
              { to: "/about-calculator", Icon: Info, label: ar ? "ما هي حاسبة برولانسر؟" : "About the calculator", test: "footer-link-about" },
              { to: "/stories", Icon: Sparkles, label: ar ? "قصص نجاح حقيقية" : "Success stories", test: "footer-link-stories" },
              { to: "/blog", Icon: BookOpen, label: ar ? "مدوّنة برولانسر" : "Prolancer blog", test: "footer-link-blog" },
            ].map(({ to, Icon, label, test }) => (
              <RouterLink
                key={test}
                to={to}
                data-testid={test}
                className="group flex items-center gap-2 rounded-xl border border-[#2e3a5c] bg-[#182138] px-3 py-2.5 hover:border-[#18c98f] transition"
              >
                <Icon className="h-4 w-4 text-[#18c98f] shrink-0" />
                <span className="font-arabic text-xs font-bold text-white/85 group-hover:text-[#18c98f] truncate">
                  {label}
                </span>
              </RouterLink>
            ))}
          </div>

          {/* Official Saudi resources — each in its own long rectangle so the
              full official name is readable at a glance. */}
          <div
            data-testid="footer-resources"
            className="mt-2 grid grid-cols-1 gap-2"
            dir="rtl"
          >
            {[
              { href: "https://prolancer.sa", Icon: Globe, label: ar ? "منصة برولانسر الرسمية" : "Prolancer official platform", test: "footer-resource-prolancer" },
              { href: "https://brah-tech.com", Icon: Globe, label: ar ? "موقع براح الرقمية" : "Barah Digital website", test: "footer-resource-brah" },
              { href: "https://freelance.sa/", Icon: Shield, label: ar ? "منصة العمل الحر — وزارة الموارد البشرية" : "Saudi Freelance Portal — MHRSD", test: "footer-resource-freelance" },
              { href: "https://www.monshaat.gov.sa/", Icon: Compass, label: ar ? "منشآت — الهيئة العامة للمنشآت الصغيرة والمتوسطة" : "Monshaat — SME General Authority", test: "footer-resource-monshaat" },
              { href: "https://zatca.gov.sa/", Icon: Shield, label: ar ? "هيئة الزكاة والضريبة والجمارك (ZATCA)" : "ZATCA — Tax Authority", test: "footer-resource-zatca" },
              { href: "https://sdaia.gov.sa/", Icon: Info, label: ar ? "الهيئة السعودية للبيانات والذكاء الاصطناعي (SDAIA)" : "SDAIA — Saudi Data & AI Authority", test: "footer-resource-sdaia" },
            ].map(({ href, Icon, label, test }) => (
              <a
                key={test}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={test}
                className="group flex items-center gap-2.5 rounded-xl border border-[#2e3a5c] bg-[#182138] px-4 py-2.5 hover:border-[#18c98f] transition"
              >
                <Icon className="h-4 w-4 text-[#18c98f] shrink-0" />
                <span className="font-arabic text-xs sm:text-sm font-bold text-white/85 group-hover:text-[#18c98f]">
                  {label}
                </span>
              </a>
            ))}
          </div>

          {/* Commercial + legal links required by the payment gateway (Tap
              Payments) and Saudi e-commerce transparency rules. */}
          <div
            data-testid="footer-policies"
            className="mt-3 grid grid-cols-2 md:grid-cols-5 gap-2"
            dir="rtl"
          >
            {[
              { to: "/services", Icon: FileText, label: ar ? "الخدمات" : "Services", test: "footer-link-services" },
              { to: "/products", Icon: Package, label: ar ? "المنتجات" : "Products", test: "footer-link-products" },
              { to: "/terms", Icon: Scale, label: ar ? "سياسة الاستخدام" : "Terms of use", test: "footer-link-terms" },
              { to: "/refund", Icon: RefreshCcw, label: ar ? "سياسة الاسترداد" : "Refund policy", test: "footer-link-refund" },
              { to: "/privacy", Icon: Shield, label: ar ? "سياسة الخصوصية" : "Privacy policy", test: "footer-link-privacy" },
            ].map(({ to, Icon, label, test }) => (
              <RouterLink
                key={test}
                to={to}
                data-testid={test}
                className="group flex items-center gap-2 rounded-xl border border-[#2e3a5c] bg-[#182138] px-3 py-2.5 hover:border-[#18c98f] transition"
              >
                <Icon className="h-4 w-4 text-[#18c98f] shrink-0" />
                <span className="font-arabic text-[11px] sm:text-xs font-bold text-white/85 group-hover:text-[#18c98f] truncate">
                  {label}
                </span>
              </RouterLink>
            ))}
          </div>

          {/* Branded social icons — moved from the "Follow us" column to live
              right under the sitemap, as the user requested. */}
          <div
            data-testid="footer-brand-socials"
            className="mt-4 flex items-center justify-center gap-3"
            dir="rtl"
          >
            <a
              href="https://www.instagram.com/prolancerplatform"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-brand-ig"
              aria-label="Prolancer Instagram"
              title="Instagram"
              className="h-11 w-11 rounded-full inline-flex items-center justify-center bg-gradient-to-tr from-[#833ab4] via-[#fd1d1d] to-[#fcb045] text-white hover:scale-110 shadow-[0_10px_24px_-10px_rgba(253,29,29,0.55)] transition"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                <path d="M12 2.2c3.2 0 3.6 0 4.8.1 1.2.1 1.9.2 2.3.4.6.2 1 .5 1.5 1s.8.9 1 1.5c.2.4.3 1.1.4 2.3.1 1.2.1 1.6.1 4.8s0 3.6-.1 4.8c-.1 1.2-.2 1.9-.4 2.3-.2.6-.5 1-1 1.5s-.9.8-1.5 1c-.4.2-1.1.3-2.3.4-1.2.1-1.6.1-4.8.1s-3.6 0-4.8-.1c-1.2-.1-1.9-.2-2.3-.4-.6-.2-1-.5-1.5-1s-.8-.9-1-1.5c-.2-.4-.3-1.1-.4-2.3C2.2 15.6 2.2 15.2 2.2 12s0-3.6.1-4.8c.1-1.2.2-1.9.4-2.3.2-.6.5-1 1-1.5s.9-.8 1.5-1c.4-.2 1.1-.3 2.3-.4C8.4 2.2 8.8 2.2 12 2.2zm0 1.8c-3.1 0-3.5 0-4.7.1-1.1.1-1.6.2-2 .3-.5.2-.8.4-1.2.8s-.6.7-.8 1.2c-.1.4-.2.9-.3 2-.1 1.2-.1 1.6-.1 4.6s0 3.4.1 4.6c.1 1.1.2 1.6.3 2 .2.5.4.8.8 1.2s.7.6 1.2.8c.4.1.9.2 2 .3 1.2.1 1.6.1 4.6.1s3.4 0 4.6-.1c1.1-.1 1.6-.2 2-.3.5-.2.8-.4 1.2-.8s.6-.7.8-1.2c.1-.4.2-.9.3-2 .1-1.2.1-1.6.1-4.6s0-3.4-.1-4.6c-.1-1.1-.2-1.6-.3-2-.2-.5-.4-.8-.8-1.2s-.7-.6-1.2-.8c-.4-.1-.9-.2-2-.3-1.2-.1-1.6-.1-4.6-.1zM12 7a5 5 0 1 1 0 10 5 5 0 0 1 0-10zm0 1.8a3.2 3.2 0 1 0 0 6.4 3.2 3.2 0 0 0 0-6.4zm5.3-2.3a1.2 1.2 0 1 1 0 2.4 1.2 1.2 0 0 1 0-2.4z" />
              </svg>
            </a>
            <a
              href="https://www.tiktok.com/@prolancer.platform"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-brand-tt"
              aria-label="Prolancer TikTok"
              title="TikTok"
              className="h-11 w-11 rounded-full inline-flex items-center justify-center bg-[#010101] text-white border border-white/10 hover:bg-black hover:scale-110 shadow-[0_10px_24px_-10px_rgba(0,0,0,0.7)] transition"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden>
                <path d="M12.9 2h2.9c.1 1 .5 2 1.1 2.8.6.8 1.4 1.3 2.4 1.6v2.9c-1.5 0-2.9-.4-4-1.1v6.4c0 3.1-2.5 5.7-5.7 5.7a5.7 5.7 0 1 1 0-11.4c.3 0 .6 0 .9.1v3c-.3-.1-.6-.1-.9-.1a2.8 2.8 0 1 0 2.8 2.8V2h.5z" fill="#25F4EE" transform="translate(-0.8 0.8)" />
                <path d="M12.9 2h2.9c.1 1 .5 2 1.1 2.8.6.8 1.4 1.3 2.4 1.6v2.9c-1.5 0-2.9-.4-4-1.1v6.4c0 3.1-2.5 5.7-5.7 5.7a5.7 5.7 0 1 1 0-11.4c.3 0 .6 0 .9.1v3c-.3-.1-.6-.1-.9-.1a2.8 2.8 0 1 0 2.8 2.8V2h.5z" fill="#FE2C55" transform="translate(0.8 -0.4)" />
                <path d="M12.9 2h2.9c.1 1 .5 2 1.1 2.8.6.8 1.4 1.3 2.4 1.6v2.9c-1.5 0-2.9-.4-4-1.1v6.4c0 3.1-2.5 5.7-5.7 5.7a5.7 5.7 0 1 1 0-11.4c.3 0 .6 0 .9.1v3c-.3-.1-.6-.1-.9-.1a2.8 2.8 0 1 0 2.8 2.8V2h.5z" fill="#FFFFFF" />
              </svg>
            </a>
            <a
              href="https://wa.me/966532556796"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-brand-wa"
              aria-label="Prolancer WhatsApp"
              title="WhatsApp"
              className="h-11 w-11 rounded-full inline-flex items-center justify-center bg-[#25D366] text-white hover:bg-[#1ebe5a] hover:scale-110 shadow-[0_10px_24px_-10px_rgba(37,211,102,0.6)] transition"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                <path d="M20.5 3.5A10.7 10.7 0 0 0 3.3 18l-1.4 5.1 5.3-1.4A10.7 10.7 0 1 0 20.5 3.5zM12 20.3c-1.6 0-3.3-.4-4.7-1.2l-.3-.2-3.1.8.8-3-.2-.3a8.6 8.6 0 1 1 7.5 3.9zm4.7-6.5c-.3-.1-1.5-.7-1.8-.8-.2-.1-.4-.1-.6.1s-.7.8-.8 1c-.2.2-.3.2-.6.1-.3-.1-1.1-.4-2.1-1.3-.8-.7-1.3-1.6-1.5-1.9-.2-.3 0-.4.1-.6.1-.1.3-.3.4-.5.1-.2.2-.3.3-.5.1-.2 0-.4 0-.5-.1-.1-.6-1.4-.8-1.9-.2-.5-.4-.4-.6-.4h-.5c-.2 0-.5.1-.7.3-.3.3-1 .9-1 2.2 0 1.3.9 2.5 1.1 2.7.1.2 1.9 2.9 4.6 4 2.7 1 2.7.7 3.1.7.5-.1 1.5-.6 1.7-1.2.2-.6.2-1.1.1-1.2-.1-.1-.3-.2-.6-.3z" />
              </svg>
            </a>
            <a
              href="mailto:info@pro-lancer.net"
              data-testid="footer-brand-support"
              aria-label="Prolancer Support — info@pro-lancer.net"
              title="info@pro-lancer.net"
              className="h-11 w-11 rounded-full inline-flex items-center justify-center bg-[#18c98f] text-white hover:bg-[#18c98f] hover:scale-110 shadow-[0_10px_24px_-10px_rgba(24,201,143,0.6)] transition"
            >
              <LifeBuoy className="h-5 w-5" strokeWidth={2.4} />
            </a>
          </div>
        </div>

        {/* Legal */}
        <div className="mt-10 text-center space-y-3" data-testid="footer-legal">
          <p
            className={`text-sm leading-relaxed ${ar ? "font-arabic" : "font-outfit"} ${
              dark ? "text-white/85" : "text-[#2d2c35]"
            }`}
          >
            {ar ? (
              <>
                <span className="font-bold">برولانسر</span>
                <sup className="mx-1 text-[#18c98f]">™</sup>
                علامة تجارية مسجّلة ملك مؤسسة{" "}
                <span className="font-bold">براح الرقمية لتقنية المعلومات</span>{" "}
                <a href="https://brah-tech.com" target="_blank" rel="noopener noreferrer" data-testid="footer-brah-link-ar" className="font-bold text-[#18c98f] hover:underline" dir="ltr">(brah-tech.com)</a>،
                سجل تجاري رقم{" "}
                <span className="font-mono-num font-bold" dir="ltr">
                  7054088864
                </span>
                .
              </>
            ) : (
              <>
                <span className="font-bold">Prolancer</span>
                <sup className="mx-1 text-[#18c98f]">™</sup>
                is a registered trademark owned by{" "}
                <span className="font-bold">Barah Digital Information Technology Est.</span>{" "}
                <a href="https://brah-tech.com" target="_blank" rel="noopener noreferrer" data-testid="footer-brah-link-en" className="font-bold text-[#18c98f] hover:underline" dir="ltr">(brah-tech.com)</a>,
                Commercial Registration No.{" "}
                <span className="font-mono-num font-bold">7054088864</span>.
              </>
            )}
          </p>
          <p
            className={`inline-flex items-center gap-1.5 text-xs ${
              dark ? "text-white/60" : "text-[#2d2c35]/60"
            }`}
          >
            <Copyright className="h-3.5 w-3.5" />
            <span>
              {new Date().getFullYear()} ·{" "}
              {ar
                ? "جميع الحقوق محفوظة لمؤسسة براح الرقمية لتقنية المعلومات"
                : "All rights reserved — Barah Digital Information Technology Est."}{" "}
              <a href="https://brah-tech.com" target="_blank" rel="noopener noreferrer" data-testid="footer-brah-link-copyright" className="font-bold text-[#18c98f] hover:underline" dir="ltr">(brah-tech.com)</a>
            </span>
          </p>
          <a
            data-testid="footer-admin-link"
            href="/admin"
            aria-label={ar ? "دخول الإدارة" : "Admin login"}
            title={ar ? "دخول الإدارة" : "Admin login"}
            className={`inline-flex items-center justify-center h-7 w-7 rounded-full mt-1 transition ${
              dark
                ? "text-white/40 hover:text-[#18c98f] hover:bg-[#18c98f]/10"
                : "text-[#2d2c35]/40 hover:text-[#12a777] hover:bg-[#12a777]/10"
            }`}
          >
            <Shield className="h-3.5 w-3.5" />
          </a>
        </div>
      </div>
    </footer>
  );
}
